import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BellIcon } from '../../Sidebar/icons';
import { useNotificationInbox } from '../../../hooks/useNotificationInbox';
import { GenericMissionIcon, getNotificationType } from '../../../notifications';
import { getInboxItemDisplay } from '../../../utils/notificationInbox';
import { navigateToCampaign, openCampaignInNewTab } from '../../../utils/navigationBridge';
import { fulfillWaitFromInbox } from '../../../utils/waitCardEditor';
import { OPEN_WAIT_REMINDER } from '../../../utils/waitFor';
import { useAuth } from '../../AuthProvider';
import { formatRelativeTime } from '../../SprintDetail/shared/CardComponents/DueDateDisplay/utils/formatRelativeTime';
import { renderIcon } from '../../SprintDetail/FlowchartView/utils/renderIcon';
import { showToast } from '../../Toast';
import './NotificationBell.css';

function useOverlayScrollThumb(active, extraKey) {
  const listRef = useRef(null);
  const thumbRef = useRef(null);
  const [thumb, setThumb] = useState(null);
  thumbRef.current = thumb;

  const sync = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      setThumb(null);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + 1) {
      setThumb(null);
      return;
    }
    const height = Math.max(28, (clientHeight / scrollHeight) * clientHeight);
    const maxTop = clientHeight - height;
    const top = maxTop * (scrollTop / (scrollHeight - clientHeight));
    setThumb({ top, height });
  }, []);

  const onThumbPointerDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const el = listRef.current;
    const current = thumbRef.current;
    if (!el || !current) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    const maxTop = el.clientHeight - current.height;
    if (maxScroll <= 0 || maxTop <= 0) return;
    const startY = event.clientY;
    const startScroll = el.scrollTop;
    const move = (next) => {
      el.scrollTop = startScroll + ((next.clientY - startY) / maxTop) * maxScroll;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    const el = listRef.current;
    if (!el) return undefined;
    const frame = window.requestAnimationFrame(sync);
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    const onWheel = (event) => {
      event.preventDefault();
      let dy = event.deltaY;
      if (event.deltaMode === 1) dy *= 10;
      else if (event.deltaMode === 2) dy *= el.clientHeight * 0.28;
      else dy *= 0.38;
      el.scrollTop += dy;
    };
    el.addEventListener('scroll', sync, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.cancelAnimationFrame(frame);
      ro.disconnect();
      el.removeEventListener('scroll', sync);
      el.removeEventListener('wheel', onWheel);
    };
  }, [active, extraKey, sync]);

  return { listRef, thumb, onThumbPointerDown };
}

/**
 * Navbar inbox. The unread count stays until a notification is opened or marked read.
 */
const NotificationBell = () => {
  const {
    items,
    unreadCount,
    markRead,
    deleteItem,
    acceptInvite,
    dismissInvite,
    inviteBusyId,
    inviteError,
  } = useNotificationInbox();
  const { workspaceId } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const { listRef, thumb, onThumbPointerDown } = useOverlayScrollThumb(open, items.length);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openItem = (item, newTab) => {
    markRead(item.id);
    setOpen(false);
    if (!item.campaignId || item.targetGoneKind === 'campaign') {
      showToast('This campaign no longer exists', { type: 'info' });
      return;
    }
    if (item.targetGone) {
      showToast('This mission no longer exists', { type: 'info' });
    }
    const sprint = { id: item.campaignId, name: item.campaignName || 'Campaign' };
    const options = item.targetGone || !item.missionId ? {} : { missionId: item.missionId };
    if (newTab) {
      openCampaignInNewTab(sprint, options);
      return;
    }
    navigateToCampaign(sprint, options);
  };

  const answerWait = async (item, fulfilled) => {
    if (fulfilled) {
      try {
        const result = await fulfillWaitFromInbox({
          campaignId: item.campaignId,
          taskId: item.missionId,
          workspaceId,
        });
        if (!result?.ok) {
          showToast('Could not mark this as fulfilled', { type: 'info' });
          return;
        }
      } catch {
        showToast('Could not mark this as fulfilled', { type: 'info' });
        return;
      }
    } else if (item.missionId && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(OPEN_WAIT_REMINDER, item.missionId);
    }
    markRead(item.id);
    setOpen(false);
    if (!item.campaignId || item.targetGoneKind === 'campaign') return;
    const sprint = { id: item.campaignId, name: item.campaignName || 'Campaign' };
    const options = item.missionId ? { missionId: item.missionId } : {};
    navigateToCampaign(sprint, options);
    if (!fulfilled && item.missionId) {
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent(OPEN_WAIT_REMINDER, { detail: { taskId: item.missionId } }));
      }, 600);
    }
  };

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div className="navbar-inbox" ref={wrapRef}>
      <button
        type="button"
        className={`navbar__settings-btn navbar-inbox__btn ${open ? 'navbar-inbox__btn--open' : ''}`}
        onClick={() => setOpen((value) => !value)}
        title="Notifications"
        aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <BellIcon size={15} />
        {unreadCount > 0 && (
          <span className="navbar-inbox__badge" aria-hidden>
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <div className="navbar-inbox__panel" role="dialog" aria-label="Notifications">
          <div className="navbar-inbox__header">
            <span className="navbar-inbox__heading">Notifications</span>
          </div>

          {inviteError && <p className="navbar-inbox__error">{inviteError}</p>}

          {items.length === 0 ? (
            <div className="navbar-inbox__empty">Nothing yet</div>
          ) : (
            <div className="navbar-inbox__scroll">
            <ul className="navbar-inbox__list" ref={listRef}>
              {items.map((item) => {
                const display = getInboxItemDisplay(item);
                const kind = getNotificationType(item.type);
                const TypeIcon = kind.Icon;
                const invite = kind.isInvite;
                const busy = inviteBusyId === item.id;
                const missionIcon = display.icon
                  ? renderIcon(display.icon, {
                      size: '22px',
                      defaultColor: '#f4f4f5',
                      fallback: <GenericMissionIcon size={22} />,
                    })
                  : null;
                const typeBase = kind.usesMissionIcon
                  ? (missionIcon || <GenericMissionIcon size={22} />)
                  : undefined;
                const body = (
                  <>
                    <span
                      className={`navbar-inbox__type${kind.usesMissionIcon ? ' navbar-inbox__type--mission' : ''}`}
                      title={kind.label}
                    >
                      <TypeIcon size={22} base={typeBase} />
                    </span>
                    <span className="navbar-inbox__body">
                      <span className="navbar-inbox__lead">
                        <span className="navbar-inbox__prefix">{display.prefix}</span>
                        {' '}
                        <span className="navbar-inbox__mission">{display.mission}</span>
                      </span>
                      {display.note && (
                        <span className="navbar-inbox__note">{display.note}</span>
                      )}
                      <span className="navbar-inbox__meta">
                        {display.path && <span>{display.path}</span>}
                        {display.path && <span className="navbar-inbox__dot" aria-hidden>·</span>}
                        <span>{formatRelativeTime(item.createdAt) || ''}</span>
                      </span>
                      {item.targetGone && (
                        <span className="navbar-inbox__gone">
                          {item.targetGoneKind === 'campaign'
                            ? 'This campaign no longer exists'
                            : 'This mission no longer exists'}
                        </span>
                      )}
                      {kind.asksFulfillment && !item.targetGone && (
                        <span className="navbar-inbox__invite-actions">
                          <button
                            type="button"
                            className="navbar-inbox__invite-btn navbar-inbox__invite-btn--accept"
                            onClick={() => answerWait(item, true)}
                          >
                            Fulfilled
                          </button>
                          <button
                            type="button"
                            className="navbar-inbox__invite-btn"
                            onClick={() => answerWait(item, false)}
                          >
                            Not yet
                          </button>
                        </span>
                      )}
                      {invite && (
                        <span className="navbar-inbox__invite-actions">
                          <button
                            type="button"
                            className="navbar-inbox__invite-btn navbar-inbox__invite-btn--accept"
                            disabled={busy}
                            onClick={() => acceptInvite(item)}
                          >
                            {busy ? 'Joining…' : 'Accept'}
                          </button>
                          <button
                            type="button"
                            className="navbar-inbox__invite-btn"
                            disabled={busy}
                            onClick={() => dismissInvite(item)}
                          >
                            Not now
                          </button>
                        </span>
                      )}
                      {!invite && (
                        <span className="navbar-inbox__actions">
                          <button
                            type="button"
                            className="navbar-inbox__action"
                            aria-label={`Open ${display.mission}`}
                            onClick={() => openItem(item, false)}
                          >
                            Open
                          </button>
                          <button
                            type="button"
                            className="navbar-inbox__action"
                            aria-label={`Open ${display.mission} in a new tab`}
                            onClick={() => openItem(item, true)}
                          >
                            Open in new tab
                          </button>
                          <button
                            type="button"
                            className="navbar-inbox__action"
                            aria-label={`Mark ${display.mission} as read`}
                            disabled={!!item.read}
                            onClick={() => markRead(item.id)}
                          >
                            Mark as read
                          </button>
                          <button
                            type="button"
                            className="navbar-inbox__action navbar-inbox__action--danger"
                            aria-label={`Delete ${display.mission}`}
                            onClick={() => deleteItem(item)}
                          >
                            Delete
                          </button>
                        </span>
                      )}
                    </span>
                  </>
                );
                return (
                  <li key={item.id}>
                    <div
                      className={[
                        'navbar-inbox__item',
                        item.read ? '' : 'navbar-inbox__item--unread',
                        !invite && item.targetGone ? 'navbar-inbox__item--gone' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {body}
                    </div>
                  </li>
                );
              })}
            </ul>
            {thumb && (
              <span
                className="navbar-inbox__thumb"
                aria-hidden
                style={{ height: thumb.height, transform: `translateY(${thumb.top}px)` }}
                onPointerDown={onThumbPointerDown}
              />
            )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
